(() => {
  if (customElements.get('up12-investment')) return;
  class Up12Investment extends HTMLElement {
    connectedCallback() {
      if (this._controller || this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        if (this.isConnected) this.initialize();
      });
    }
    disconnectedCallback() {
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = null;
      this._controller?.abort();
      this._controller = null;
    }
    initialize() {
      this._rows = Array.from(this.querySelectorAll('[data-up12-row]')).map(row => ({
        select: row.querySelector('[data-up12-product]'),
        quantity: row.querySelector('[data-up12-quantity]'),
        line: row.querySelector('[data-up12-line]')
      }));
      this._fieldset = this.querySelector('[data-up12-fields]');
      this._error = this.querySelector('[data-up12-error]');
      this._note = this.querySelector('[data-up12-payment-note]');
      if (!this._fieldset || !this._error || !this._note || this._rows.length !== 3 || this._rows.some(row => !row.select || !row.quantity || !row.line)) return;
      this._money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this._controller = new AbortController();
      const signal = this._controller.signal;
      const update = event => {
        if (!event.target.matches('[data-up12-product], [data-up12-quantity]')) return;
        this.dataset.up12CalculationUsed = 'true';
        this.calculate();
      };
      this.addEventListener('input', update, { signal });
      this.addEventListener('change', update, { signal });
      this.addEventListener('click', event => {
        const link = event.target instanceof Element ? event.target.closest('a[data-up12-consult]') : null;
        if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
        this.dataset.up12CalculationUsed = 'true';
        this.calculate();
      }, { capture: true, signal });
      this._fieldset.disabled = false;
      this.calculate();
    }
    format(cents) {
      return this._money.format(cents / 100);
    }
    fill(template, values) {
      return template.replace(/\[([a-z]+)\]/g, (token, key) => Object.hasOwn(values, key) ? values[key] : token);
    }
    output(name, value) {
      const node = this.querySelector('[data-up12-output="' + name + '"]');
      if (node) node.textContent = value;
    }
    publish(detail) {
      this.dataset.up12CalculationValid = String(detail.valid);
      this.dataset.up12CalculationSummary = detail.summary;
      if (this.dataset.up12CalculationUsed === 'true') {
        this.dispatchEvent(new CustomEvent('up12:investment', { bubbles: true, detail: { ...detail, used: true, sourceId: this.id } }));
      }
    }
    invalidate(message) {
      this._rows.forEach(row => { row.line.textContent = ''; });
      ['garments', 'deposit', 'total'].forEach(name => this.output(name, this.dataset.notCalculated));
      this._note.textContent = '';
      this._note.hidden = true;
      this._error.textContent = message;
      this._error.hidden = false;
      this.publish({ valid: false, summary: '', rows: [] });
    }
    calculate() {
      const rows = this._rows.map(row => {
        const raw = row.quantity.value.trim();
        const quantity = Number(raw);
        const valid = raw !== '' && !row.quantity.validity.badInput && Number.isInteger(quantity) && quantity >= 0 && quantity <= 999;
        row.quantity.setAttribute('aria-invalid', String(!valid));
        return { ...row, productId: row.select.value, quantityValue: quantity, valid };
      });
      if (rows.some(row => !row.valid)) {
        this.invalidate(this.dataset.quantityError);
        return;
      }
      const products = new Map();
      let invalidPricing = false;
      for (const option of this._rows[0].select.options) {
        const values = [option.dataset.priceSmall, option.dataset.priceMedium, option.dataset.priceLarge];
        const prices = values.map(value => Number(value));
        if (!option.value || !option.textContent.trim() || products.has(option.value) || values.some(value => value === undefined || value.trim() === '') || prices.some(price => !Number.isSafeInteger(price) || price < 0)) invalidPricing = true;
        products.set(option.value, { title: option.textContent.trim(), prices });
      }
      const depositCents = Number(this.dataset.depositCents);
      if (!Number.isSafeInteger(depositCents) || depositCents < 0 || products.size === 0 || rows.some(row => !products.has(row.productId))) invalidPricing = true;
      if (invalidPricing) {
        this.invalidate(this.dataset.pricingError);
        return;
      }
      const grouped = new Map();
      rows.forEach(row => grouped.set(row.productId, (grouped.get(row.productId) || 0) + row.quantityValue));
      let garmentTotalCents = 0;
      const resultRows = rows.map(row => {
        const product = products.get(row.productId);
        const productQuantity = grouped.get(row.productId);
        const unitCents = product.prices[productQuantity < 10 ? 0 : productQuantity < 24 ? 1 : 2];
        const lineCents = unitCents * row.quantityValue;
        garmentTotalCents += lineCents;
        return { productId: row.productId, product: product.title, quantity: row.quantityValue, productQuantity, unitCents, lineCents };
      });
      const totalCents = garmentTotalCents + depositCents;
      if (!Number.isSafeInteger(garmentTotalCents) || !Number.isSafeInteger(totalCents) || resultRows.some(row => !Number.isSafeInteger(row.lineCents))) {
        this.invalidate(this.dataset.pricingError);
        return;
      }
      this._error.hidden = true;
      this._error.textContent = '';
      const summaryRows = [];
      resultRows.forEach((row, index) => {
        const values = { product: row.product, quantity: String(row.quantity), unit: this.format(row.unitCents), line: this.format(row.lineCents) };
        this._rows[index].line.textContent = row.quantity ? this.fill(this.dataset.lineTemplate, values) : this.dataset.notIncluded;
        if (row.quantity) summaryRows.push(this.fill(this.dataset.summaryLine, values));
      });
      this.output('garments', this.format(garmentTotalCents));
      this.output('deposit', this.format(depositCents));
      this.output('total', this.format(totalCents));
      const hasGarments = resultRows.some(row => row.quantity > 0);
      const values = { garments: this.format(garmentTotalCents), deposit: this.format(depositCents), total: this.format(totalCents), rows: summaryRows.join('; ') || this.dataset.notIncluded };
      this._note.textContent = this.fill(hasGarments ? this.dataset.paymentNote : this.dataset.emptyNote, values);
      this._note.hidden = false;
      this.publish({ valid: true, summary: this.fill(this.dataset.summaryTemplate, values), rows: resultRows, garmentTotalCents, depositCents, totalCents });
    }
  }
  customElements.define('up12-investment', Up12Investment);
})();
